import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AddressInfo } from '@/lib/crypto/pqp-hd-wallet'

export interface WalletData {
  id: string
  name: string
  type: 'hd' | 'imported' | 'watch-only'
  createdAt: Date
  lastActivity: Date
  addresses: {
    external: AddressInfo[]
    change: AddressInfo[]
  }
  accountIndex: number
  testnet: boolean
  // For HD wallets - store xpub hash as identifier
  xpubHash?: string
  // For watch-only - store dpub
  dpub?: string
}

interface WalletManagerState {
  wallets: WalletData[]
  activeWalletId: string | null
  
  // Add a new wallet
  addWallet: (wallet: Omit<WalletData, 'id' | 'createdAt' | 'lastActivity'>) => string
  
  // Update wallet data
  updateWallet: (id: string, updates: Partial<WalletData>) => void
  
  // Remove a wallet
  removeWallet: (id: string) => void
  
  // Set active wallet
  setActiveWallet: (id: string) => void
  
  // Get active wallet
  getActiveWallet: () => WalletData | null
  
  // Get wallet by id
  getWallet: (id: string) => WalletData | null
  
  // Update wallet addresses
  updateWalletAddresses: (id: string, addresses: WalletData['addresses']) => void
  
  // Clear all wallets
  clearAll: () => void
}

export const useWalletManager = create<WalletManagerState>()(
  persist(
    (set, get) => ({
      wallets: [],
      activeWalletId: null,
      
      addWallet: (walletData) => {
        const id = crypto.randomUUID()
        const now = new Date()
        const wallet: WalletData = {
          ...walletData,
          id,
          createdAt: now,
          lastActivity: now
        }
        
        set((state) => ({
          wallets: [...state.wallets, wallet],
          activeWalletId: wallet.id
        }))
        
        return id
      },
      
      updateWallet: (id, updates) => {
        set((state) => ({
          wallets: state.wallets.map(w => 
            w.id === id 
              ? { ...w, ...updates, lastActivity: new Date() }
              : w
          )
        }))
      },
      
      removeWallet: (id) => {
        set((state) => {
          const newWallets = state.wallets.filter(w => w.id !== id)
          const newActiveId = state.activeWalletId === id 
            ? (newWallets.length > 0 ? newWallets[0].id : null)
            : state.activeWalletId
            
          return {
            wallets: newWallets,
            activeWalletId: newActiveId
          }
        })
      },
      
      setActiveWallet: (id) => {
        const wallet = get().wallets.find(w => w.id === id)
        if (wallet) {
          set({ activeWalletId: id })
        }
      },
      
      getActiveWallet: () => {
        const state = get()
        if (!state.activeWalletId) return null
        return state.wallets.find(w => w.id === state.activeWalletId) || null
      },
      
      getWallet: (id) => {
        return get().wallets.find(w => w.id === id) || null
      },
      
      updateWalletAddresses: (id, addresses) => {
        set((state) => ({
          wallets: state.wallets.map(w => 
            w.id === id 
              ? { ...w, addresses, lastActivity: new Date() }
              : w
          )
        }))
      },
      
      clearAll: () => {
        set({ wallets: [], activeWalletId: null })
      }
    }),
    {
      name: 'wallet-manager',
      version: 1
    }
  )
)